import { useEffect, useMemo, useRef, useState } from "react"
import { useNavigate, useOutletContext, useSearchParams } from "react-router-dom"
import moment from "moment"
import nProgress from "nprogress"

import { useAuth } from "contexts/auth-context"
import { Pagination } from "components"
import { lang } from "config"
import { usePage } from "contexts/page-context"

import Table from "components/table"

import useStockTransactionRepositories from "repositories/stock-transaction"
import Filter from "./filter"

export default function List() {
  const { can } = useAuth()
  const { configuration } = usePage()
  const { title, transition, setTransition } = useOutletContext()

  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  const searchInput = useRef(null)
  const searchDebounceRef = useRef(null)

  const page = searchParams.get("page")
  const search = searchParams.get("search")

  const product = searchParams.get("product_unit_id")
  const outlet = searchParams.get("outlet_id")
  const referenceType = searchParams.get("reference_type")

  const dateFrom = searchParams.get("date_from")
  const dateTo = searchParams.get("date_to")

  const orderBy = searchParams.get("order_by")
  const direction = searchParams.get("direction")

  const [searchText, setSearchText] = useState(search ?? "")

  const { data: stockRaw, isLoading } = useStockTransactionRepositories({
    page,
    search,
    product_unit_id: product,
    outlet_id: outlet,
    reference_type: referenceType,
    date_from: dateFrom,
    date_to: dateTo,
    order_by: orderBy,
    direction,
  })

  const stockData = stockRaw ?? {}

  useEffect(() => {
    if (isLoading) nProgress.start()
    else nProgress.done()
  }, [isLoading])

  useEffect(() => {
    searchInput.current?.focus()
  }, [])

  useEffect(() => {
    setSearchText(search ?? "")
  }, [search])

  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current)
      }
    }
  }, [])

  const mergeParams = (next) => {
    const current = Object.fromEntries(searchParams.entries())
    const merged = { ...current, ...next }

    Object.keys(merged).forEach((key) => {
      if (merged[key] === null || merged[key] === undefined || merged[key] === "") {
        delete merged[key]
      }
    })

    setSearchParams(merged)
  }

  const commitSearch = (value) => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current)
    }

    mergeParams({
      search: value || null,
      page: 1,
    })
  }

  const updateSearch = (value) => {
    setSearchText(value)

    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current)
    }

    searchDebounceRef.current = setTimeout(() => {
      mergeParams({
        search: value || null,
        page: 1,
      })
    }, 400)
  }

  const clearSearch = () => {
    setSearchText("")
    commitSearch("")
    searchInput.current?.focus()
  }

  const toggleSort = (value) => {
    if ((direction === null || direction === "desc") || orderBy !== value) {
      mergeParams({
        order_by: value,
        direction: "asc",
        page: 1,
      })
    } else {
      mergeParams({
        order_by: value,
        direction: "desc",
        page: 1,
      })
    }
  }

  const dataListHeaders = [
    { isAction: false, content: "Reference", sortKey: "reference_id" },
    { isAction: false, content: "Product", sortKey: "product_unit_id" },
    { isAction: false, content: "Outlet", sortKey: "outlet_id" },
    { isAction: false, content: "Type", sortKey: "reference_type" },
    { isAction: false, content: "Qty", sortKey: "quantity" },
    { isAction: false, content: "Balance", sortKey: "current_stock" },
    { isAction: false, content: "Transaction Date", sortKey: "transaction_date" },
    { isAction: false, content: "Created By", sortKey: "created_by" },
  ]

  const dataListColumns = [
    {
      type: "default",
      bold: true,
      content: (v) => v.reference_id || "-",
    },

    {
      type: "default",
      content: (v) => v.product_unit?.name || "-",
    },

    {
      type: "default",
      content: (v) => v.outlet?.name || "-",
    },

    {
      type: "default",
      content: (v) => (
        <span className="px-2 py-1 text-xs rounded bg-neutral-100">
          {v.reference_type}
        </span>
      ),
    },

    {
      type: "default",
      content: (v) => {
        const qty = Number(v.quantity ?? 0)

        return (
          <span
            className={
              qty >= 0
                ? "text-green-600 font-semibold"
                : "text-red-600 font-semibold"
            }
          >
            {qty >= 0 ? `+${qty}` : qty}
          </span>
        )
      },
    },

    {
      type: "default",
      content: (v) => (
        <span className="font-semibold text-neutral-700">
          {v.current_stock ?? "-"}
        </span>
      ),
    },

    {
      type: "default",
      content: (v) => {
        const d = v.transaction_date
        return d ? moment(d).format("YYYY-MM-DD HH:mm") : "-"
      },
    },

    {
      type: "default",
      content: (v) => v.created_by || "-",
    },
  ]

  const updateFilter = (value) => {
    mergeParams({ ...value, page: 1 })
  }

  const removeFilter = () => {
    setSearchParams({})
    setSearchText("")
  }

  const handleExport = () => {
    const params = new URLSearchParams(searchParams)
    const url = `${process.env.REACT_APP_INVENTORY_API_URL}/stock/transfer/export?${params.toString()}`
    window.open(url, "_blank")
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="relative">
          <input
            ref={searchInput}
            onChange={(e) => updateSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                commitSearch(searchText)
              }
            }}
            value={searchText}
            type="text"
            placeholder="Search reference..."
            className="w-72 py-3 pl-4 pr-10 text-sm border rounded-xl border-neutral-200 focus:outline-none focus:ring"
          />

          {!!searchText && (
            <button
              type="button"
              onClick={clearSearch}
              className="absolute inset-y-0 right-3 flex items-center text-neutral-400 hover:text-neutral-700"
            >
              ✕
            </button>
          )}
        </div>

        <div className="flex items-center gap-2">
          <Filter
            onSubmit={updateFilter}
            onRemove={removeFilter}
            data={Object.fromEntries(
              Object.entries({
                outlet,
                product,
                referenceType,
                dateFrom,
                dateTo,
              }).filter(([_, v]) => v != null)
            )}
          />

          {can("stock_transaction.export") && (
            <button
              onClick={handleExport}
              className="inline-flex items-center px-4 py-3 space-x-2 text-white transition bg-green-600 rounded-xl active:hover:scale-95"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-4 h-4"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
                fill="none"
              >
                <path d="M12 3v12" />
                <path d="M7 10l5 5l5 -5" />
                <path d="M5 21h14" />
              </svg>

              <span>Export</span>
            </button>
          )}
        </div>
      </div>

      <Table
        isLoading={isLoading}
        data={stockData?.data ?? []}
        headers={dataListHeaders}
        columns={dataListColumns}
        key={(v) => v.reference_id}
        isEmpty={!stockData?.data?.length && !search}
        isEmptyOnSearching={!stockData?.data?.length && !!search}
        toggleSort={toggleSort}
        orderBy={orderBy}
        direction={direction}
      />

      <Pagination
        links={stockData?.links}
        from={stockData?.from}
        to={stockData?.to}
        total={stockData?.total}
      />
    </div>
  )
}